<template>
  <BModal
    v-model="isVisible"
    modal-class="zoomIn"
    hide-footer
    no-close-on-backdrop
    centered
    size="lg"
    title="Modifier l'entreprise"
    @hidden="onClose"
  >
    <BForm @submit.prevent="handleSubmit">
      <BRow>
        <BCol cols="12" md="6" class="mb-3">
          <label class="form-label"
            >Raison sociale <span class="required-star">*</span></label
          >
          <BFormInput
            v-model="form.legalName"
            :class="{ 'is-invalid': errors.legalName }"
            placeholder="Raison sociale"
            required
          />
          <div v-if="errors.legalName" class="invalid-feedback">
            {{ errors.legalName }}
          </div>
        </BCol>

        <BCol cols="12" md="6" class="mb-3">
          <label class="form-label">Gouvernorat</label>
          <BFormInput v-model="form.governorate" placeholder="Gouvernorat" />
        </BCol>

        <BCol cols="12" md="6" class="mb-3">
          <label class="form-label">Activité principale</label>
          <BFormInput
            v-model="form.mainActivitySector"
            placeholder="Activité principale"
          />
        </BCol>

        <BCol cols="12" md="6" class="mb-3">
          <label class="form-label">Secteur primaire</label>
          <BFormInput
            v-model="form.primarySector"
            placeholder="Secteur primaire"
          />
        </BCol>

        <BCol cols="12" md="6" class="mb-3">
          <label class="form-label">Secteur secondaire</label>
          <BFormInput
            v-model="form.secondarySector"
            placeholder="Secteur secondaire"
          />
        </BCol>

        <BCol cols="12" md="6" class="mb-3">
          <label class="form-label">Email</label>
          <BFormInput
            v-model="form.email"
            type="email"
            placeholder="email@example.com"
          />
        </BCol>

        <BCol cols="12" md="6" class="mb-3">
          <label class="form-label">Site web</label>
          <BFormInput
            v-model="form.website"
            placeholder="https://example.com"
          />
        </BCol>

        <BCol cols="12" md="6" class="mb-3">
          <label class="form-label">Année de création</label>
          <BFormInput
            v-model.number="form.yearFounded"
            type="number"
            placeholder="2020"
            min="1800"
            max="2026"
          />
        </BCol>

        <BCol cols="12" class="mb-3">
          <label class="form-label">Adresse</label>
          <BFormTextarea
            v-model="form.address"
            placeholder="Adresse complète"
            rows="2"
          />
        </BCol>
      </BRow>

      <div class="d-flex gap-2 justify-content-end mt-4">
        <BButton variant="light" @click="handleCancel" :disabled="saving">
          Annuler
        </BButton>
        <BButton variant="primary" type="submit" :disabled="saving">
          <span
            v-if="saving"
            class="spinner-border spinner-border-sm me-1"
          ></span>
          {{ saving ? 'Enregistrement...' : 'Enregistrer' }}
        </BButton>
      </div>
    </BForm>
  </BModal>
</template>

<script>
import { companyService } from '@/shared/services/company.service';
import notificationService from '@/shared/services/notification.service';

export default {
  name: 'EditCompanyModal',
  props: {
    modelValue: { type: Boolean, default: false },
    company: { type: Object, default: null },
  },
  emits: ['update:modelValue', 'updated'],
  data() {
    return {
      saving: false,
      form: {
        legalName: '',
        governorate: '',
        mainActivitySector: '',
        primarySector: '',
        secondarySector: '',
        email: '',
        website: '',
        address: '',
        yearFounded: null,
      },
      errors: {},
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.modelValue;
      },
      set(v) {
        this.$emit('update:modelValue', v);
      },
    },
  },
  watch: {
    company: {
      immediate: true,
      handler(newCompany) {
        if (newCompany) {
          this.form = {
            legalName: newCompany.legalName || '',
            governorate: newCompany.governorate || '',
            mainActivitySector: newCompany.mainActivitySector || '',
            primarySector: newCompany.primarySector || '',
            secondarySector: newCompany.secondarySector || '',
            email: newCompany.email || '',
            website: newCompany.website || '',
            address: newCompany.address || '',
            yearFounded: newCompany.yearFounded || null,
          };
        }
      },
    },
  },
  methods: {
    handleCancel() {
      this.isVisible = false;
      this.errors = {};
    },
    onClose() {
      this.$emit('update:modelValue', false);
      this.errors = {};
    },
    async handleSubmit() {
      this.errors = {};

      if (!this.form.legalName || this.form.legalName.trim() === '') {
        this.errors.legalName = 'La raison sociale est obligatoire';
        return;
      }

      if (!this.company?.id) return;

      this.saving = true;
      try {
        const payload = {
          legalName: this.form.legalName?.trim(),
          governorate: this.form.governorate?.trim() || undefined,
          mainActivitySector: this.form.mainActivitySector?.trim() || undefined,
          primarySector: this.form.primarySector?.trim() || undefined,
          secondarySector: this.form.secondarySector?.trim() || undefined,
          email: this.form.email?.trim() || undefined,
          website: this.form.website?.trim() || undefined,
          address: this.form.address?.trim() || undefined,
          yearFounded: this.form.yearFounded || undefined,
        };

        Object.keys(payload).forEach((key) => {
          if (payload[key] === undefined || payload[key] === '') {
            delete payload[key];
          }
        });

        const updatedCompany = await companyService.updateCompany(
          this.company.id,
          payload,
        );
        this.isVisible = false;
        // L'enregistrement ne disait rien: la modale se fermait, et rien ne
        // distinguait une fiche mise a jour d'une modale simplement annulee.
        notificationService.successAction('update', 'company');
        this.$emit('updated', updatedCompany);
      } catch (err) {
        console.error('Erreur complète:', err);
        console.error('Réponse du serveur:', err.response?.data);
        const message =
          err.response?.data?.message || 'Erreur lors de la mise à jour.';
        notificationService.errorAction('update', 'company', message);

        if (err.response?.data?.errors) {
          this.errors = err.response.data.errors;
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Meme marque que dans le questionnaire: l'etoile d'un champ obligatoire est rouge. */
.required-star {
  color: #dc3545;
  font-weight: 600;
}

.form-label {
  font-weight: 500;
  font-size: 14px;
  margin-bottom: 6px;
  color: #5a5c69;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}
</style>
